import { useEffect, useRef, useState, type ReactNode } from "react";
import { apiFetch } from "../../services/apiUtils";
import { SellerReportDrawer } from "./SellerReportDrawer";
import { SellerReportDatePicker } from "./SellerReportDatePicker";
import {
  ReportDocument,
  type FrozenSellerReport,
  type ReportImages,
} from "./ReportDocument";
import {
  formatSellerReportDateTime,
  formatSellerReportPeriod,
} from "./formatters";
import type { ReportTerminology } from "./reportTypes";
import { PortalPerformance } from './PortalPerformance';

type Suggestion = {
  listingContactId: string;
  name: string | null;
  email: string | null;
  mobile: string | null;
  contactFingerprint: string;
};
type Recipient =
  | {
      recipientKey: string;
      source: "MANUAL";
      name: string;
      email?: string;
      mobile?: string;
    }
  | {
      recipientKey: string;
      source: "LISTING_CONTACT";
      listingContactId: string;
      contactFingerprint: string;
      recipientLabel?: string;
    };
type Grant = {
  id?: string;
  grantId?: string;
  recipient_name?: string;
  recipient_email?: string | null;
  recipient_mobile?: string | null;
  recipientName?: string;
  expiresAt?: string | null;
  expires_at?: string | null;
  revokedAt?: string | null;
  revoked_at?: string | null;
  secureReportUrl?: string | null;
  whatsappMobile?: string | null;
  whatsappAvailable?: boolean;
};
type History = {
  id: string;
  report_version: number;
  period_from: string;
  period_to: string;
  generated_at: string;
  generated_by_display_name: string;
  status: string;
  active_grants: number;
  total_grants: number;
};
type Prepared = {
  reportFingerprint: string;
  suggestedRecipients: Suggestion[];
  aiDraftAvailable: boolean;
  reportPreview?: { portalPerformance?: import('./PortalPerformance').PortalPerformanceData };
};
type GmailStatus = {
  configured: boolean;
  connected: boolean;
  connectedEmail: string | null;
  expectedEmail: string;
  deliveryMode: "gmail" | "dry-run";
  senderName: string;
};
type EmailResult = { id: string; status: string; recipientName?: string; errorCode?: string };
type CommunicationAttempt = { id: string; recipient_name: string; requested_by_name: string; channel: string; status: string; provider: string; created_at: string; submitted_at: string | null };
type EmailPreview = { subject: string; html: string; text: string; filename: string | null };
type WhatsAppPreview = { recipientName: string | null; mobile: string; message: string };

export function gmailCallbackMessage(result: string | null): string | null {
  switch (result) {
    case "connected": return "Your approved KWSA Google Workspace account is connected.";
    case "domain-not-approved": return "Please connect your approved KWSA Google Workspace account (@kwsa.co.za or @kwsouthafrica.co.za).";
    case "ownership-mismatch":
    case "email-mismatch": return "Connect the Google account that belongs to your MAPP associate profile.";
    case "send-scope-required": return "Gmail send permission is required to connect this account.";
    case "denied": return "Google account connection was cancelled.";
    case "state-invalid":
    case "missing-code":
    case "callback-failed": return "Gmail could not be connected. Please try again or contact support.";
    default: return null;
  }
}

function CommunicationHistory({ items }: { items: CommunicationAttempt[] }) {
  if (!items.length) return null;
  return <section aria-label="Communication history" className="space-y-2 border-t border-slate-200 pt-4">
    <h3 className="font-semibold text-slate-950">Communication history</h3>
    {items.map((item) => {
      const whatsapp = item.channel === "WHATSAPP";
      const status = whatsapp
        ? item.status === "LAUNCH_REQUESTED" ? "WhatsApp launch requested" : "WhatsApp status unavailable"
        : item.status === "SUBMITTED_TEST" ? "Dry-run only" : item.status === "SUBMITTED" ? "Submitted"
          : item.status === "SUBMISSION_UNKNOWN" ? "Submission unknown" : item.status;
      return <article key={item.id} className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 py-2 text-sm">
        <div className="min-w-0"><p className="font-medium text-slate-900">{item.recipient_name}</p><p className="text-xs text-slate-500">Requested by {item.requested_by_name} · {whatsapp ? "WhatsApp" : item.provider === "LOCAL_DRY_RUN" ? "Local dry-run" : "Gmail"}</p></div>
        <div className="text-right"><p className="font-semibold text-slate-800">{status}</p><p className="text-xs text-slate-500">{formatSellerReportDateTime(whatsapp ? item.created_at : item.submitted_at ?? item.created_at)}</p></div>
      </article>;
    })}
  </section>;
}

export function selectedReportRecipients(
  suggestions: Suggestion[],
  selected: Record<string, string>,
  manual: Recipient[],
  labels: Record<string, string> = {},
): Recipient[] {
  return [
    ...suggestions
      .filter((row) => selected[row.listingContactId])
      .map((row) => ({
        source: "LISTING_CONTACT" as const,
        recipientKey: selected[row.listingContactId],
        listingContactId: row.listingContactId,
        contactFingerprint: row.contactFingerprint,
        ...(!row.name
          ? { recipientLabel: labels[row.listingContactId] || "" }
          : {}),
      })),
    ...manual,
  ];
}

export function canOpenSellerReportLink(
  grant: Grant,
  now = Date.now(),
): boolean {
  if (!grant.secureReportUrl || grant.revokedAt || grant.revoked_at)
    return false;
  const expiry = grant.expiresAt ?? grant.expires_at;
  if (
    expiry &&
    (!Number.isFinite(Date.parse(expiry)) || Date.parse(expiry) <= now)
  )
    return false;
  try {
    const url = new URL(grant.secureReportUrl);
    return (
      (url.protocol === "https:" ||
        (url.protocol === "http:" &&
          ["localhost", "127.0.0.1"].includes(url.hostname))) &&
      /^\/seller-report\/[A-Za-z0-9_-]{43}$/.test(url.pathname)
    );
  } catch {
    return false;
  }
}

export function replacementGrantRequest(grant: Grant, recipientKey = crypto.randomUUID()) {
  const name = grant.recipient_name ?? grant.recipientName ?? "Recipient";
  const id = grant.id ?? grant.grantId;
  return {
    recipients: [{
      source: "MANUAL" as const,
      recipientKey,
      name,
      ...(grant.recipient_email ? { email: grant.recipient_email } : {}),
      ...(grant.recipient_mobile ? { mobile: grant.recipient_mobile } : {}),
    }],
    ...(!grant.revoked_at && !grant.revokedAt && id ? { replaceGrantId: id } : {}),
  };
}

export async function copySellerReportLink(
  grant: Grant,
  writeText: (value: string) => Promise<void>,
  now = Date.now(),
): Promise<boolean> {
  if (!canOpenSellerReportLink(grant, now) || !grant.secureReportUrl)
    return false;
  await writeText(grant.secureReportUrl);
  return true;
}

export function SellerReportGrantActions({
  grant,
  onCopy,
  reportTitle,
  primary = true,
  showView = true,
}: {
  grant: Grant;
  onCopy: () => void;
  reportTitle: string;
  primary?: boolean;
  showView?: boolean;
}) {
  const available = canOpenSellerReportLink(grant);
  return (
    <div className="flex flex-wrap gap-2">
      {showView && available ? (
        <a
          href={grant.secureReportUrl!}
          target="_blank"
          rel="noopener noreferrer"
          referrerPolicy="no-referrer"
          className={`flex min-h-11 items-center justify-center rounded px-4 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 ${primary ? "bg-red-800 text-white hover:bg-red-900" : "border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"}`}
        >
          View {reportTitle}
        </a>
      ) : showView ? (
        <span className="flex min-h-11 items-center text-xs text-slate-500">
          Link unavailable. Replace it to share again.
        </span>
      ) : null}
      <button
        type="button"
        disabled={!available}
        onClick={onCopy}
        className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Copy Link
      </button>
    </div>
  );
}

export default function SellerReportWorkflow({
  listingId,
  token,
  contextId,
  from,
  to,
  notes,
  onNotesChange,
  onFromChange,
  onToChange,
  terminology,
  children,
}: {
  listingId: string;
  token: string;
  contextId: string;
  from: string;
  to: string;
  notes: string;
  onNotesChange: (text: string) => void;
  onFromChange: (date: string) => void;
  onToChange: (date: string) => void;
  terminology: ReportTerminology;
  children?: ReactNode;
}) {
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [manual, setManual] = useState<Recipient[]>([]);
  const [linkManual, setLinkManual] = useState<Recipient[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState(true);
  const [history, setHistory] = useState<History[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [receipt, setReceipt] = useState<{
    reportVersionId: string;
    reportVersion: number;
    generatedAt: string;
    periodFrom: string;
    periodTo: string;
    shareGrants: Grant[];
  } | null>(null);
  const [frozen, setFrozen] = useState<{
    reportVersionId: string;
    report: FrozenSellerReport;
    shareGrants: Grant[];
    communications?: CommunicationAttempt[];
  } | null>(null);
  const [drawer, setDrawer] = useState<"prepare" | "links" | "history" | "email" | "whatsapp" | null>(null);
  const [historyPreview, setHistoryPreview] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [emailGrantIds, setEmailGrantIds] = useState<string[]>([]);
  const [emailPreview, setEmailPreview] = useState<EmailPreview | null>(null);
  const [attachPdf, setAttachPdf] = useState(false);
  const [emailIdempotencyKey, setEmailIdempotencyKey] = useState("");
  const [emailResults, setEmailResults] = useState<EmailResult[]>([]);
  const [whatsappGrantId, setWhatsappGrantId] = useState("");
  const [whatsappPreview, setWhatsappPreview] = useState<WhatsAppPreview | null>(null);
  const [whatsappIdempotencyKey, setWhatsappIdempotencyKey] = useState("");
  const [whatsappPopupBlocked, setWhatsappPopupBlocked] = useState(false);
  const [images, setImages] = useState<ReportImages>({});
  const revision = useRef(0);
  const base = `/api/listings/${encodeURIComponent(listingId)}/seller-reports`;
  const headers = {
    Authorization: `Bearer ${token}`,
    "X-Active-Context": contextId,
    "Content-Type": "application/json",
  };
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("gmail");
    if (!result) return;
    const message = gmailCallbackMessage(result);
    if (message) setGmailNotice(message);
    params.delete("gmail");
    const query = params.toString();
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
  }, []);
  async function request(path: string, options?: RequestInit) {
    const currentRevision = revision.current;
    const response = await apiFetch(path, {
      ...options,
      headers: { ...headers, ...options?.headers },
    });
    if (currentRevision !== revision.current)
      throw new Error("Report context changed.");
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.error || "Seller report is unavailable.");
    }
    const result = response.status === 204 ? null : await response.json();
    if (currentRevision !== revision.current)
      throw new Error("Report context changed.");
    return result;
  }
  async function loadHistory(cursor?: number) {
    const result = await request(`${base}${cursor ? `?cursor=${cursor}` : ""}`);
    setHistory((current) =>
      cursor ? [...current, ...result.items] : result.items,
    );
    setNextCursor(result.nextCursor);
  }
  useEffect(() => {
    let current = true;
    revision.current++;
    setPrepared(null);
    setSelected({});
    setLabels({});
    setManual([]);
    setLinkManual([]);
    setReviewed(false);
    setReceipt(null);
    setFrozen(null);
    setError("");
    setHistory([]);
    setAvailable(true);
    setBusy(false);
    const params = new URLSearchParams({ periodFrom: from, periodTo: to });
    apiFetch(`${base}/prepare?${params}`, { headers })
      .then(async (response) => {
        if (!current) return;
        if (response.status === 404) {
          setAvailable(false);
          return;
        }
        if (!response.ok) {
          setError("Report preparation could not be loaded.");
          return;
        }
        const result = await response.json();
        if (current) setPrepared(result);
        const list = await apiFetch(base, { headers });
        if (current && list.ok) {
          const result = await list.json();
          setHistory(result.items);
          setNextCursor(result.nextCursor);
        }
      })
      .catch(() => {
        if (current) setError("Report preparation could not be loaded.");
      });
    return () => {
      current = false;
    };
  }, [listingId, contextId, token, from, to]);
  useEffect(() => {
    setReviewed(false);
  }, [notes]);
  useEffect(() => {
    setImages({});
    if (!frozen) return;
    let alive = true;
    const urls: string[] = [];
    for (const kind of ["property", "agent", "logo"] as const)
      if (frozen.report.assets[kind]) {
        apiFetch(`${base}/${frozen.reportVersionId}/assets/${kind}`, {
          headers,
        })
          .then(async (response) => {
            if (!response.ok) return;
            const url = URL.createObjectURL(await response.blob());
            urls.push(url);
            if (alive) setImages((current) => ({ ...current, [kind]: url }));
            else URL.revokeObjectURL(url);
          })
          .catch(() => undefined);
      }
    return () => {
      alive = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [frozen, base, token, contextId]);
  const run = async (work: () => Promise<void>) => {
    const currentRevision = revision.current;
    setBusy(true);
    setError("");
    try {
      await work();
    } catch (failure) {
      if (currentRevision === revision.current) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Report action could not be completed.",
        );
        setReviewed(false);
      }
    } finally {
      if (currentRevision === revision.current) setBusy(false);
    }
  };
  const recipients = prepared
    ? selectedReportRecipients(
        prepared.suggestedRecipients,
        selected,
        manual,
        labels,
      )
    : [];
  const missingName = recipients.some((row) =>
    row.source === "MANUAL" ? !row.name.trim() : row.recipientLabel === "",
  );
  const linkRecipients = [...recipients, ...linkManual];
  const linkMissingName = linkRecipients.some((row) => row.source === "MANUAL" && !row.name.trim());
  const pdf = async (reportId: string, print: boolean) => {
    const viewer = print ? window.open("", "_blank") : null;
    const response = await apiFetch(`${base}/${reportId}/download`, {
      headers,
    });
    if (!response.ok) {
      viewer?.close();
      throw new Error("PDF unavailable.");
    }
    const url = URL.createObjectURL(await response.blob());
    if (print && viewer) {
      viewer.opener = null;
      viewer.location.href = url;
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.download = "seller-report.pdf";
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const revokeGrant = async (reportId: string, grantId: string) => {
    await request(`${base}/${reportId}/share-grants/${grantId}`, {
      method: "DELETE",
    });
    setReceipt((current) =>
      current?.reportVersionId === reportId
        ? {
            ...current,
            shareGrants: current.shareGrants.filter(
              (item) => item.grantId !== grantId,
            ),
          }
        : current,
    );
    if (frozen?.reportVersionId === reportId)
      setFrozen(await request(`${base}/${reportId}`));
    await loadHistory();
  };
  const revokeAllGrants = async (reportId: string) => {
    await request(`${base}/${reportId}/share-grants`, { method: "DELETE" });
    setReceipt((current) =>
      current?.reportVersionId === reportId
        ? { ...current, shareGrants: [] }
        : current,
    );
    if (frozen?.reportVersionId === reportId)
      setFrozen(await request(`${base}/${reportId}`));
    await loadHistory();
  };
  const openManageLinks = async (reportId: string) => {
    setHistoryPreview(false);
    setFrozen(await request(`${base}/${reportId}`));
    setDrawer("links");
  };
  const loadEmailPreview = async (reportId: string, grantId: string, includePdf: boolean) => {
    setEmailPreview(await request(`${base}/${reportId}/email/preview/${grantId}?attachPdf=${includePdf}`));
  };
  const openEmail = async (reportId: string) => {
    await run(async () => {
      const [report, status] = await Promise.all([
        request(`${base}/${reportId}`),
        request(`${base}/gmail/status`),
      ]);
      setFrozen(report);
      setGmailStatus(status);
      const eligible = report.shareGrants.filter((grant: Grant) => {
        const expiresAt = grant.expires_at ?? grant.expiresAt;
        return Boolean(grant.id && grant.recipient_email && !grant.revoked_at && !grant.revokedAt && (!expiresAt || Date.parse(expiresAt) > Date.now()));
      });
      const grantIds = eligible.map((grant: Grant) => grant.id!);
      setEmailGrantIds(grantIds);
      setAttachPdf(false);
      setEmailPreview(null);
      if (grantIds[0]) await loadEmailPreview(reportId, grantIds[0], false);
      setEmailIdempotencyKey(crypto.randomUUID());
      setEmailResults([]);
      setDrawer("email");
    });
  };
  const changeEmailRecipients = (next: string[]) => {
    setEmailGrantIds(next);
    if (!frozen || !next[0]) { setEmailPreview(null); return; }
    void run(() => loadEmailPreview(frozen.reportVersionId, next[0], attachPdf));
  };
  const changeEmailAttachment = (includePdf: boolean) => {
    setAttachPdf(includePdf);
    if (!frozen || !emailGrantIds[0]) return;
    void run(() => loadEmailPreview(frozen.reportVersionId, emailGrantIds[0], includePdf));
  };
  const connectGmail = async () => {
    await run(async () => {
      const result = await request("/api/communications/gmail/connect", {
        method: "POST",
        body: JSON.stringify({ returnPath: window.location.pathname }),
      });
      window.location.assign(result.url);
    });
  };
  const disconnectGmail = async () => {
    await run(async () => {
      await request("/api/communications/gmail/disconnect", { method: "DELETE" });
      setGmailStatus((current) => current ? { ...current, connected: false, connectedEmail: null } : current);
    });
  };
  const sendReportEmail = async () => {
    if (!frozen || !emailGrantIds.length || !emailIdempotencyKey) return;
    await run(async () => {
      const result = await request(`${base}/${frozen.reportVersionId}/email`, {
        method: "POST",
        body: JSON.stringify({ shareGrantIds: emailGrantIds, idempotencyKey: emailIdempotencyKey, attachPdf }),
      });
      setEmailResults(result.results);
      setFrozen(await request(`${base}/${frozen.reportVersionId}`));
      await loadHistory();
    });
  };
  const openWhatsApp = async (reportId: string) => {
    await run(async () => {
      setFrozen(await request(`${base}/${reportId}`));
      setWhatsappGrantId("");
      setWhatsappPreview(null);
      setWhatsappIdempotencyKey("");
      setWhatsappPopupBlocked(false);
      setDrawer("whatsapp");
    });
  };
  const previewWhatsApp = async (reportId: string, grantId: string) => {
    await run(async () => {
      setWhatsappPreview(await request(`${base}/${reportId}/whatsapp/preview/${grantId}`));
      setWhatsappGrantId(grantId);
      setWhatsappIdempotencyKey(crypto.randomUUID());
      setWhatsappPopupBlocked(false);
    });
  };
  const launchWhatsApp = () => {
    if (!frozen || !whatsappGrantId || !whatsappIdempotencyKey) return;
    const popup = window.open("about:blank", "_blank");
    if (!popup) {
      setWhatsappPopupBlocked(true);
      return;
    }
    popup.opener = null;
    setWhatsappPopupBlocked(false);
    const reportId = frozen.reportVersionId;
    const grantId = whatsappGrantId;
    const idempotencyKey = whatsappIdempotencyKey;
    void run(async () => {
      try {
        const result = await request(`${base}/${reportId}/whatsapp/${grantId}/launch`, {
          method: "POST",
          body: JSON.stringify({ idempotencyKey }),
        });
        popup.location.replace(result.launchUrl);
        setFrozen(await request(`${base}/${reportId}`));
        await loadHistory();
      } catch (failure) {
        popup.close();
        throw failure;
      }
    });
  };
  const latestReady = history.find((row) => row.status === "READY");
  const currentIssuedLink = receipt?.shareGrants.find((grant) => canOpenSellerReportLink(grant));
  const currentReportId = receipt?.reportVersionId ?? latestReady?.id;
  const [gmailNotice, setGmailNotice] = useState("");
  if (!available) return (
    <section aria-label="Generate and manage seller reports" className="space-y-5">
      <p role="status" className="border-y border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        Report generation and link management are unavailable for this backend. The Seller Activity preview remains available.
      </p>
      {children}
    </section>
  );
  const piiError = /buyer-identifying information/i.test(error);
  return (
    <section
      aria-label="Generate and manage seller reports"
      className="space-y-7"
    >
      <header className="flex min-w-0 flex-col gap-4 border-y border-slate-200 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 sm:flex-1">
          <p className="text-xs font-semibold uppercase text-red-800">{terminology.reportTitle}</p>
          {latestReady ? (
            <p className="mt-1 text-sm text-slate-600">Version {latestReady.report_version} · {formatSellerReportPeriod(latestReady.period_from, latestReady.period_to)} · {latestReady.active_grants} active links</p>
          ) : <p className="mt-1 text-sm text-slate-500">No report generated yet</p>}
        </div>
        <div aria-label="Seller report actions" className="flex min-w-0 flex-wrap gap-2 sm:justify-end">
          <button type="button" disabled={busy} onClick={() => setDrawer("prepare")} className="min-h-11 w-full rounded bg-red-800 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
            Prepare &amp; Generate
          </button>
          <button type="button" disabled={!latestReady || busy} onClick={() => latestReady && run(() => openManageLinks(latestReady.id))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-40">Manage Links</button>
          <button type="button" disabled={!history.length || busy} onClick={() => setDrawer("history")} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-40">Report History</button>
        </div>
      </header>
      {children}
      {currentReportId && latestReady && (
        <div aria-label="Current report actions" className="flex flex-col gap-4 border-b border-slate-200 py-4 sm:flex-row sm:items-end sm:justify-between">
          {currentIssuedLink && <div aria-label="Secure report link" className="flex min-w-0 flex-wrap items-center gap-3"><span className="text-xs font-semibold uppercase text-slate-500">View</span><SellerReportGrantActions grant={currentIssuedLink} primary={false} reportTitle={terminology.reportTitle} onCopy={() => run(async () => { await copySellerReportLink(currentIssuedLink, (value) => navigator.clipboard.writeText(value)); })} /></div>}
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div role="group" aria-label="Share report" className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="mr-1 w-full text-[11px] font-semibold uppercase text-slate-500 sm:w-auto">Share</span>
              <button type="button" disabled={busy} onClick={() => openEmail(currentReportId)} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Send via Email</button>
              <button type="button" disabled={busy} onClick={() => openWhatsApp(currentReportId)} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">Share via WhatsApp</button>
            </div>
            <div role="group" aria-label="Report document" className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="mr-1 w-full text-[11px] font-semibold uppercase text-slate-500 sm:w-auto">Document</span>
              <button type="button" onClick={() => run(() => pdf(currentReportId, false))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50">Download PDF</button>
              <button type="button" onClick={() => run(() => pdf(currentReportId, true))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50">Print</button>
            </div>
          </div>
        </div>
      )}
      {error && !piiError && (
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {gmailNotice && <p role="status" className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{gmailNotice}</p>}
      <SellerReportDrawer open={drawer === "prepare"} title={receipt ? `${terminology.reportTitle} Ready` : "Prepare & Generate"} onClose={() => setDrawer(null)}>
      {!prepared ? (
        <p role="status" className="text-sm text-slate-500">
          Preparing report…
        </p>
      ) : (
        <>
          <section
            aria-label="Step 1 Prepare"
            className="space-y-5 border-t border-slate-200 pt-5"
          >
            <header className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                1
              </span>
              <div>
                <h3 className="font-semibold text-slate-950">Prepare</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Choose the period, write the {terminology.updateTitle} and select which {terminology.recipientLabel.toLowerCase()}{" "}
                  should receive it.
                </p>
              </div>
            </header>
            <div className="grid gap-4 rounded border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
              <SellerReportDatePicker label="Reporting period starts" value={from} max={to} onChange={(value) => { onFromChange(value); setReviewed(false); }} />
              <SellerReportDatePicker label="Reporting period ends" value={to} min={from} onChange={(value) => { onToChange(value); setReviewed(false); }} />
              <p className="text-sm text-slate-600 sm:col-span-2">{formatSellerReportPeriod(from, to)}</p>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-slate-900">
                  {terminology.recipientLabel} recipients
                </h4>
                <p className="text-sm font-medium text-slate-600">
                  {recipients.length === 0 ? "No recipients selected" : `${recipients.length} ${terminology.recipientLabel.toLowerCase()}${recipients.length === 1 ? "" : "s"} selected`}
                </p>
              </div>
              <fieldset className="space-y-2">
                <legend className="sr-only">{terminology.recipientLabel} contacts</legend>
                {prepared.suggestedRecipients.length ? (
                  prepared.suggestedRecipients.map((row) => {
                    const checked = Boolean(selected[row.listingContactId]);
                    return (
                      <label
                        key={row.listingContactId}
                        className={`flex min-h-[68px] cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors ${checked ? "border-red-700 bg-red-50/60" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) => {
                            setSelected((current) => {
                              const next = { ...current };
                              if (event.target.checked)
                                next[row.listingContactId] =
                                  crypto.randomUUID();
                              else delete next[row.listingContactId];
                              return next;
                            });
                            setReviewed(false);
                          }}
                          className="mt-1"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block break-words font-semibold text-slate-900">
                            {row.name || "Listing contact"}
                          </span>
                          {(row.email || row.mobile) && (
                            <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                              {row.email && (
                                <span className="break-all">{row.email}</span>
                              )}
                              {row.mobile && <span>{row.mobile}</span>}
                            </span>
                          )}
                        </span>
                        <span
                          className={`shrink-0 pt-0.5 text-xs font-semibold ${checked ? "text-red-800" : "text-slate-500"}`}
                        >
                          {checked ? "Selected" : "Select"}
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <p className="border-y border-dashed border-slate-300 py-5 text-sm text-slate-500">
                    No {terminology.recipientLabel.toLowerCase()} contacts are available. Add a manual recipient
                    below.
                  </p>
                )}
              </fieldset>
              {prepared.suggestedRecipients
                .filter((row) => selected[row.listingContactId] && !row.name)
                .map((row) => (
                  <label
                    key={row.listingContactId}
                    className="block text-sm font-medium text-slate-700"
                  >
                    {terminology.recipientLabel} label
                    <input
                      value={labels[row.listingContactId] || ""}
                      maxLength={200}
                      onChange={(event) => {
                        setLabels((current) => ({
                          ...current,
                          [row.listingContactId]: event.target.value,
                        }));
                        setReviewed(false);
                      }}
                      className="mt-1 min-h-11 w-full rounded-md border border-slate-300 px-3"
                    />
                  </label>
                ))}
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-slate-900">
                  Manual {terminology.recipientLabel.toLowerCase()} recipients
                </h4>
                <button
                  type="button"
                  disabled={recipients.length >= 20}
                  onClick={() => {
                    setManual((current) => [
                      ...current,
                      {
                        source: "MANUAL",
                        recipientKey: crypto.randomUUID(),
                        name: "",
                      },
                    ]);
                    setReviewed(false);
                  }}
                  className="min-h-11 rounded border border-slate-300 px-4 text-sm font-medium disabled:opacity-40"
                >
                  Add recipient
                </button>
              </div>
              {manual.map(
                (row, index) =>
                  row.source === "MANUAL" && (
                    <div
                      key={row.recipientKey}
                      className="grid min-w-0 gap-3 rounded-md border border-slate-200 p-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
                    >
                      <label className="min-w-0 text-xs font-medium text-slate-600">
                        {terminology.recipientLabel} name
                        <input
                          value={row.name}
                          maxLength={200}
                          onChange={(event) => {
                            setManual((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? { ...row, name: event.target.value }
                                  : item,
                              ),
                            );
                            setReviewed(false);
                          }}
                          className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900"
                        />
                      </label>
                      <label className="min-w-0 text-xs font-medium text-slate-600">
                        Email
                        <input
                          type="email"
                          value={row.email || ""}
                          onChange={(event) => {
                            setManual((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? {
                                      ...row,
                                      email: event.target.value || undefined,
                                    }
                                  : item,
                              ),
                            );
                            setReviewed(false);
                          }}
                          className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900"
                        />
                      </label>
                      <label className="min-w-0 text-xs font-medium text-slate-600">
                        Mobile
                        <input
                          value={row.mobile || ""}
                          onChange={(event) => {
                            setManual((current) =>
                              current.map((item, position) =>
                                position === index
                                  ? {
                                      ...row,
                                      mobile: event.target.value || undefined,
                                    }
                                  : item,
                              ),
                            );
                            setReviewed(false);
                          }}
                          className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setManual((current) =>
                            current.filter((_, position) => position !== index),
                          );
                          setReviewed(false);
                        }}
                        className="min-h-11 self-end px-3 text-sm font-medium text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ),
              )}
            </div>
            <div className="space-y-3 border-t border-slate-200 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label
                  htmlFor="seller-update"
                  className="text-base font-semibold text-slate-950"
                >
                  {terminology.updateTitle}
                </label>
                <span className="text-xs tabular-nums text-slate-500">
                  {notes.length}/2000
                </span>
              </div>
              <textarea
                id="seller-update"
                value={notes}
                maxLength={2000}
                onChange={(event) => {
                  onNotesChange(event.target.value);
                  setReviewed(false);
                  if (piiError) setError("");
                }}
                rows={5}
                className="block min-h-32 w-full resize-y rounded-md border border-slate-300 px-3 py-3 text-sm leading-relaxed text-slate-900 focus:border-red-700 focus:outline-none focus:ring-1 focus:ring-red-700"
                placeholder={`Summarise the recorded activity for the ${terminology.audienceNoun.toLowerCase()}.`}
              />
              {piiError && (
                <p
                  role="alert"
                  className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                >
                  {error}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={busy || !prepared.aiDraftAvailable}
                  onClick={() =>
                    run(async () => {
                      const result = await request(`${base}/draft-update`, {
                        method: "POST",
                        body: JSON.stringify({
                          periodFrom: from,
                          periodTo: to,
                        }),
                      });
                      onNotesChange(result.draftText);
                      setPrepared((current) =>
                        current
                          ? {
                              ...current,
                              reportFingerprint: result.reportFingerprint,
                            }
                          : current,
                      );
                      setReviewed(false);
                    })
                  }
                  className="min-h-11 rounded border border-slate-300 px-4 text-sm font-semibold disabled:opacity-40"
                >
                  Draft {terminology.updateTitle} with AI
                </button>
                {!prepared.aiDraftAvailable && (
                  <span className="text-sm text-slate-500">
                    AI drafting unavailable
                  </span>
                )}
              </div>
            </div>
          </section>
          <section
            aria-label="Step 2 Review"
            className="space-y-6 border-t border-slate-200 pt-7"
          >
            <header className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                2
              </span>
              <div>
                <h3 className="font-semibold text-slate-950">Review</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Confirm the period, recipients and {terminology.updateTitle} before
                  generating.
                </p>
              </div>
            </header>
            <div className="grid gap-px overflow-hidden rounded border border-slate-200 bg-slate-200 sm:grid-cols-3">
              <div className="min-w-0 bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Reporting period</p>
                <p className="mt-1 break-words font-semibold text-slate-950">{formatSellerReportPeriod(from, to)}</p>
              </div>
              <div className="min-w-0 bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Recipients</p>
                <p className="mt-1 font-semibold text-slate-950">{recipients.length === 0 ? "No recipients selected" : `${recipients.length} ${terminology.recipientLabel.toLowerCase()}${recipients.length === 1 ? "" : "s"} selected`}</p>
              </div>
              <div className="min-w-0 bg-slate-50 px-4 py-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Report data</p>
                <p className="mt-1 font-semibold text-slate-950">{prepared.reportFingerprint ? "Ready to capture" : "Preparing"}</p>
              </div>
            </div>
            {prepared.reportPreview?.portalPerformance && <PortalPerformance data={prepared.reportPreview.portalPerformance} />}
            {recipients.length === 0 && <p className="rounded border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-600">You can generate the report now and create sharing links later.</p>}
            {recipients.length > 0 && (
              <ul
                aria-label={`Selected ${terminology.recipientLabel.toLowerCase()} recipients`}
                className="flex flex-wrap gap-2"
              >
                {recipients.map((recipient) => (
                  <li
                    key={recipient.recipientKey}
                    className="max-w-full rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
                  >
                    {recipient.source === "MANUAL"
                      ? recipient.name || `${terminology.recipientLabel} name required`
                      : prepared.suggestedRecipients.find(
                          (row) =>
                            row.listingContactId === recipient.listingContactId,
                        )?.name ||
                        recipient.recipientLabel ||
                        `${terminology.recipientLabel} contact`}
                  </li>
                ))}
              </ul>
            )}
            <div
              className={`rounded border p-4 ${reviewed ? "border-emerald-700 bg-emerald-50/50" : "border-slate-300 bg-white"}`}
            >
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={reviewed}
                  onChange={(event) => setReviewed(event.target.checked)}
                />
                <span>
                  <span className="block font-semibold text-slate-950">
                    {reviewed ? "Ready to generate" : "Confirm your review"}
                  </span>
                  <span className="mt-1 block text-sm text-slate-700">
                    {recipients.length === 0 ? `I’ve reviewed the reporting period and ${terminology.updateTitle}.` : `I’ve reviewed the reporting period, recipients and ${terminology.updateTitle}.`}
                  </span>
                </span>
              </label>
              <p className="ml-7 mt-2 text-xs text-slate-500">
                Buyer-identifying information must not be included.
              </p>
            </div>
          </section>
          {!receipt && <section
            aria-label="Step 3 Generate and share"
            className="space-y-6 border-t border-slate-200 pt-7"
          >
            <header className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                3
              </span>
              <div>
                <h3 className="font-semibold text-slate-950">
                  Generate &amp; Share
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">The report and PDF are frozen as one version. Sharing links can be created now or later.</p>
              </div>
            </header>
            <div className="border-t border-slate-200 pt-5">
              {recipients.length === 0 && <p className="mb-3 text-sm text-slate-600">No recipients selected. You can create sharing links after the report is ready.</p>}
              <button
              type="button"
              disabled={busy || !reviewed || missingName}
              onClick={() =>
                run(async () => {
                  const result = await request(base, {
                    method: "POST",
                    headers: { "Idempotency-Key": crypto.randomUUID() },
                    body: JSON.stringify({
                      periodFrom: from,
                      periodTo: to,
                      sellerUpdateText: notes,
                      reportFingerprint: prepared.reportFingerprint,
                      reviewConfirmed: true,
                      recipients,
                    }),
                  });
                  setReceipt({ ...result, periodFrom: from, periodTo: to });
                  await loadHistory();
                })
              }
              className="min-h-12 w-full rounded bg-red-800 px-6 text-sm font-semibold text-white transition-colors hover:bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-40 sm:ml-auto sm:block sm:w-auto"
              >{busy ? "Generating report…" : `Generate ${terminology.reportTitle}`}</button>
            </div>
          </section>}
        </>
      )}
      {receipt && (
        <section
          aria-label="Report ready"
          className="space-y-6 rounded border border-slate-200 border-t-4 border-t-emerald-700 bg-white p-5 shadow-sm sm:p-6"
        >
          <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase text-emerald-800">
                Ready
              </p>
              <h3 className="mt-1 break-words text-xl font-semibold text-slate-950">
                {terminology.reportTitle} — Version {receipt.reportVersion}
              </h3>
              <p className="mt-1 text-sm text-slate-700">
                {formatSellerReportPeriod(receipt.periodFrom, receipt.periodTo)}
              </p>
            </div>
            <button type="button" disabled={busy} onClick={() => run(async () => { setFrozen(await request(`${base}/${receipt.reportVersionId}`)); setHistoryPreview(true); setDrawer("history"); })} className="min-h-11 w-full rounded bg-red-800 px-5 text-sm font-semibold text-white transition-colors hover:bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">View {terminology.reportTitle}</button>
          </header>
          {receipt.shareGrants.length === 0
            ? <p className="-mt-2 text-sm text-slate-600">No recipients selected. You can create sharing links later.</p>
            : <p className="-mt-2 text-sm text-slate-600">{receipt.shareGrants.length} {terminology.recipientLabel.toLowerCase()}{receipt.shareGrants.length === 1 ? "" : "s"} selected</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <section aria-label="Ready report sharing actions" className="space-y-3 rounded border border-slate-200 p-4">
              <h4 className="text-xs font-semibold uppercase text-slate-500">Share</h4>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={() => openEmail(receipt.reportVersionId)} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-40">Send via Email</button>
                <button type="button" disabled={busy} onClick={() => openWhatsApp(receipt.reportVersionId)} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-40">Share via WhatsApp</button>
              </div>
            </section>
            <section aria-label="Ready report document actions" className="space-y-3 rounded border border-slate-200 p-4">
              <h4 className="text-xs font-semibold uppercase text-slate-500">Document</h4>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => run(() => pdf(receipt.reportVersionId, false))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50">Download PDF</button>
                <button type="button" onClick={() => run(() => pdf(receipt.reportVersionId, true))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50">Print</button>
              </div>
            </section>
            <section aria-label="Ready report management actions" className="space-y-3 rounded border border-slate-200 p-4 sm:col-span-2">
              <h4 className="text-xs font-semibold uppercase text-slate-500">Manage</h4>
              <button type="button" disabled={busy} onClick={() => run(() => openManageLinks(receipt.reportVersionId))} className="min-h-11 rounded border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-40">Manage Links</button>
            </section>
          </div>
          {receipt.shareGrants.length > 0 && <section aria-label="Generated recipient links" className="space-y-3 border-t border-slate-200 pt-5"><h4 className="text-sm font-semibold text-slate-900">Secure recipient links</h4>{receipt.shareGrants.map((grant) => <div key={grant.grantId} className="flex min-w-0 flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3 text-sm"><span className="min-w-0 break-words font-medium text-slate-800">{grant.recipientName}</span><SellerReportGrantActions grant={grant} showView={false} reportTitle={terminology.reportTitle} onCopy={() => run(async () => { await copySellerReportLink(grant, (value) => navigator.clipboard.writeText(value)); })} /></div>)}</section>}
        </section>
      )}
      </SellerReportDrawer>
      <SellerReportDrawer open={drawer === "history"} title="Report History" onClose={() => { setDrawer(null); setHistoryPreview(false); }}>
        {historyPreview && frozen ? (
          <div className="space-y-4">
            <button type="button" onClick={() => { setHistoryPreview(false); setFrozen(null); }} className="min-h-11 rounded border border-slate-300 px-4 text-sm">Back to history</button>
            <ReportDocument report={frozen.report} images={images} download={() => run(() => pdf(frozen.reportVersionId, false))} onPrint={() => run(() => pdf(frozen.reportVersionId, true))} />
            <button type="button" onClick={() => { setHistoryPreview(false); setDrawer("links"); }} className="min-h-11 rounded border border-slate-300 px-4 text-sm font-semibold">Manage Links</button>
          </div>
        ) : (
          <div className="space-y-2">
            {history.length ? history.map((row) => (
              <article key={row.id} className="flex min-w-0 flex-wrap items-center justify-between gap-4 border-b border-slate-200 py-4 text-sm">
                <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-slate-950">Version {row.report_version}</p><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.status === "READY" ? "bg-emerald-50 text-emerald-800" : row.status === "FAILED" ? "bg-red-50 text-red-800" : "bg-slate-100 text-slate-700"}`}>{row.status === "READY" ? "Ready" : row.status === "FAILED" ? "Failed" : "Generating"}</span></div>
                  <p className="mt-1 text-slate-700">{formatSellerReportPeriod(row.period_from,row.period_to)}</p><p className="mt-1 text-xs text-slate-500">Generated {formatSellerReportDateTime(row.generated_at)} · {row.active_grants} active {terminology.recipientLabel.toLowerCase()} links</p></div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={row.status !== "READY"} onClick={() => run(async () => { setFrozen(await request(`${base}/${row.id}`)); setHistoryPreview(true); })} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Preview</button>
                  <button type="button" disabled={row.status !== "READY"} onClick={() => run(() => pdf(row.id,false))} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Download PDF</button>
                  <button type="button" disabled={row.status !== "READY"} onClick={() => openEmail(row.id)} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Send via Email</button>
                  <button type="button" disabled={row.status !== "READY"} onClick={() => run(() => openManageLinks(row.id))} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Manage Links</button>
                </div>
              </article>
            )) : <p className="py-4 text-sm text-slate-500">No reports generated yet.</p>}
            {nextCursor && <button type="button" onClick={() => run(() => loadHistory(nextCursor))} className="min-h-11 rounded border border-slate-300 px-4 text-sm">More reports</button>}
          </div>
        )}
      </SellerReportDrawer>
      <SellerReportDrawer open={drawer === "links"} title={frozen ? `Manage ${terminology.recipientLabel} Links · Version ${frozen.report.reportVersion}` : `Manage ${terminology.recipientLabel} Links`} onClose={() => setDrawer(null)}>
        {!frozen ? <p role="status" className="text-sm text-slate-500">Choose a ready version from Report History.</p> : (
          <div className="space-y-5">
            <header className="border-b border-slate-200 pb-4"><p className="font-semibold text-slate-950">{formatSellerReportPeriod(frozen.report.period.from,frozen.report.period.to)}</p><p className="mt-1 text-sm text-slate-500">Generated {formatSellerReportDateTime(frozen.report.generatedAt)}</p></header>
            <section aria-label={`Create links for selected ${terminology.recipientLabel.toLowerCase()}s`} className="space-y-3 rounded-md border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3"><h3 className="font-semibold text-slate-900">Create links</h3><span className="text-sm font-medium text-slate-600">{linkRecipients.length} selected</span></div>
              {recipients.length > 0 && <ul aria-label={`Selected ${terminology.recipientLabel.toLowerCase()} contacts`} className="flex flex-wrap gap-2">{recipients.map((recipient) => <li key={recipient.recipientKey} className="max-w-full break-words rounded border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700">{recipient.source === "MANUAL" ? recipient.name || "Name required" : prepared?.suggestedRecipients.find(row => row.listingContactId === recipient.listingContactId)?.name || recipient.recipientLabel || `${terminology.recipientLabel} contact`}</li>)}</ul>}
              {linkManual.map((row,index)=>row.source === "MANUAL" && <div key={row.recipientKey} className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
                <label className="min-w-0 text-xs font-medium text-slate-600">{terminology.recipientLabel} name<input value={row.name} maxLength={200} onChange={event=>setLinkManual(current=>current.map((item,pos)=>pos===index?{...row,name:event.target.value}:item))} className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900" /></label>
                <label className="min-w-0 text-xs font-medium text-slate-600">Email<input type="email" value={row.email||""} onChange={event=>setLinkManual(current=>current.map((item,pos)=>pos===index?{...row,email:event.target.value||undefined}:item))} className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900" /></label>
                <label className="min-w-0 text-xs font-medium text-slate-600">Mobile<input value={row.mobile||""} onChange={event=>setLinkManual(current=>current.map((item,pos)=>pos===index?{...row,mobile:event.target.value||undefined}:item))} className="mt-1 min-h-11 w-full rounded border border-slate-300 px-3 text-sm text-slate-900" /></label>
                <button type="button" onClick={()=>setLinkManual(current=>current.filter((_,pos)=>pos!==index))} className="min-h-11 self-end px-2 text-sm text-red-800">Remove</button>
              </div>)}
              <div className="flex flex-wrap gap-2"><button type="button" disabled={linkRecipients.length>=20} onClick={()=>setLinkManual(current=>[...current,{source:"MANUAL",recipientKey:crypto.randomUUID(),name:""}])} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Add manual {terminology.recipientLabel.toLowerCase()}</button><button type="button" disabled={busy||!linkRecipients.length||linkMissingName} onClick={()=>run(async()=>{const result=await request(`${base}/${frozen.reportVersionId}/share-grants`,{method:"POST",headers:{"Idempotency-Key":crypto.randomUUID()},body:JSON.stringify({recipients:linkRecipients})});setReceipt({...result,reportVersionId:frozen.reportVersionId,reportVersion:frozen.report.reportVersion,generatedAt:frozen.report.generatedAt,periodFrom:frozen.report.period.from,periodTo:frozen.report.period.to});setLinkManual([]);setFrozen(await request(`${base}/${frozen.reportVersionId}`));await loadHistory();})} className="min-h-11 rounded bg-red-800 px-4 text-sm font-semibold text-white disabled:opacity-40">Create links</button></div>
            </section>
            <div className="flex items-center justify-between gap-3"><h3 className="font-semibold text-slate-900">{terminology.recipientLabel} recipients</h3><button type="button" disabled={busy || !frozen.shareGrants.some(g => !g.revoked_at)} onClick={() => { if(window.confirm(`Revoke all active ${terminology.recipientLabel.toLowerCase()} links for this version?`)) run(() => revokeAllGrants(frozen.reportVersionId)); }} className="min-h-11 rounded border border-slate-300 px-3 text-sm text-red-800 disabled:opacity-40">Revoke all</button></div>
            {frozen.shareGrants.length ? frozen.shareGrants.map((grant) => {
              const issued = receipt?.reportVersionId === frozen.reportVersionId ? receipt.shareGrants.find(item => item.grantId === grant.id) : undefined;
              const linkGrant = {...grant, secureReportUrl: issued?.secureReportUrl ?? null};
              const expired=Boolean(grant.expires_at && Date.parse(grant.expires_at)<=Date.now());
              const status=grant.revoked_at?"Revoked":expired?"Expired":"Active";
              return <article key={grant.id} className="space-y-3 border-b border-slate-200 py-4"><div className="flex flex-wrap items-start justify-between gap-3"><div className="min-w-0"><p className="break-words font-semibold text-slate-950">{grant.recipient_name}</p>{(grant.recipient_email||grant.recipient_mobile)&&<p className="mt-1 break-words text-sm text-slate-600">{[grant.recipient_email,grant.recipient_mobile].filter(Boolean).join(" · ")}</p>}</div><span className={`shrink-0 text-xs font-semibold ${status==="Active"?"text-emerald-800":status==="Expired"?"text-amber-800":"text-slate-500"}`}>{status}</span></div>
                {canOpenSellerReportLink(linkGrant)?<SellerReportGrantActions grant={linkGrant} reportTitle={terminology.reportTitle} onCopy={()=>run(async()=>{await copySellerReportLink(linkGrant,value=>navigator.clipboard.writeText(value));})}/>:<p className="text-xs text-slate-500">{grant.revoked_at?"This link was revoked.":expired?"This link has expired.":"The one-time token is not available after issuance. Replace it to create a new secure link."}</p>}
                <div className="flex flex-wrap gap-2"><button type="button" disabled={busy} onClick={()=>{if(!grant.revoked_at&&!window.confirm(`Replace the link for ${grant.recipient_name}? The existing link will stop working.`))return;run(async()=>{const result=await request(`${base}/${frozen.reportVersionId}/share-grants`,{method:"POST",headers:{"Idempotency-Key":crypto.randomUUID()},body:JSON.stringify(replacementGrantRequest(grant))});setReceipt({...result,reportVersionId:frozen.reportVersionId,reportVersion:frozen.report.reportVersion,generatedAt:frozen.report.generatedAt,periodFrom:frozen.report.period.from,periodTo:frozen.report.period.to});setFrozen(await request(`${base}/${frozen.reportVersionId}`));await loadHistory();});}} className="min-h-11 rounded border border-slate-300 px-3 text-sm">{grant.revoked_at?"Create replacement":"Replace Link"}</button><button type="button" disabled={busy||Boolean(grant.revoked_at)} onClick={()=>run(()=>revokeGrant(frozen.reportVersionId,grant.id!))} className="min-h-11 rounded border border-slate-300 px-3 text-sm text-red-800 disabled:opacity-40">Revoke</button></div>
              </article>;
            }):<p className="border-y border-dashed border-slate-300 py-5 text-sm text-slate-500">No {terminology.recipientLabel.toLowerCase()} links have been created for this report.</p>}
          </div>
        )}
      </SellerReportDrawer>
      <SellerReportDrawer open={drawer === "whatsapp"} title={frozen ? `Share ${frozen.report.terminology?.reportTitle ?? terminology.reportTitle} via WhatsApp` : "Share via WhatsApp"} onClose={() => setDrawer(null)}>
        {!frozen ? <p role="status" className="text-sm text-slate-500">Loading report recipients…</p> : (
          <div className="space-y-5">
            <section aria-label="WhatsApp recipients" className="space-y-3">
              <h3 className="font-semibold text-slate-950">Recipients</h3>
              {frozen.shareGrants.filter((grant: Grant) => grant.id && !grant.revoked_at && !grant.revokedAt
                && (!(grant.expires_at ?? grant.expiresAt) || Date.parse((grant.expires_at ?? grant.expiresAt)!) > Date.now())).map((grant: Grant) => (
                <article key={grant.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 py-3">
                  <div className="min-w-0"><p className="break-words font-semibold text-slate-900">{grant.recipient_name ?? grant.recipientName ?? "Recipient"}</p>
                    <p className="break-all text-sm text-slate-600">{grant.whatsappMobile ?? grant.recipient_mobile ?? "No mobile number"}</p>
                    <p className={`text-xs font-medium ${grant.whatsappAvailable ? "text-emerald-800" : "text-amber-800"}`}>{grant.whatsappAvailable ? "WhatsApp available" : "Mobile missing or invalid"}</p>
                  </div>
                  <button type="button" disabled={busy || !grant.whatsappAvailable} onClick={() => previewWhatsApp(frozen.reportVersionId, grant.id!)} className="min-h-11 rounded border border-slate-300 px-4 text-sm font-semibold disabled:opacity-40">Share</button>
                </article>
              ))}
              {!frozen.shareGrants.some((grant: Grant) => grant.id && !grant.revoked_at && !grant.revokedAt
                && (!(grant.expires_at ?? grant.expiresAt) || Date.parse((grant.expires_at ?? grant.expiresAt)!) > Date.now())) && <p className="text-sm text-slate-500">No active recipient links are available for this report.</p>}
              {frozen.shareGrants.some((grant: Grant) => !grant.whatsappAvailable && !grant.revoked_at && !grant.revokedAt
                && (!(grant.expires_at ?? grant.expiresAt) || Date.parse((grant.expires_at ?? grant.expiresAt)!) > Date.now())) && <p className="text-sm text-amber-900">A valid mobile number is required for WhatsApp sharing.</p>}
            </section>
            {whatsappPreview && <section aria-label="WhatsApp message preview" className="space-y-3 border-y border-slate-200 py-4">
              <h3 className="font-semibold text-slate-950">Message preview</h3>
              <p className="text-sm text-slate-600">Recipient: <strong className="text-slate-900">{whatsappPreview.recipientName || "Hello"}</strong> · {whatsappPreview.mobile}</p>
              <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-800">{whatsappPreview.message}</pre>
              {whatsappPopupBlocked && <p role="alert" className="text-sm text-red-800">WhatsApp could not be opened. Allow pop-ups and try again.</p>}
              <button type="button" disabled={busy} onClick={launchWhatsApp} className="min-h-11 rounded bg-red-800 px-5 text-sm font-semibold text-white disabled:opacity-40">Open WhatsApp</button>
            </section>}
            <CommunicationHistory items={frozen.communications ?? []} />
          </div>
        )}
      </SellerReportDrawer>
      <SellerReportDrawer open={drawer === "email"} title={frozen ? `Send ${frozen.report.terminology?.reportTitle ?? terminology.reportTitle} · Version ${frozen.report.reportVersion}` : "Send via Email"} onClose={() => setDrawer(null)}>
        {!frozen ? <p role="status" className="text-sm text-slate-500">Choose a ready report version.</p> : (
          <div className="space-y-6">
            <section aria-label="Gmail connection" className="space-y-3 border-b border-slate-200 pb-5">
              <h3 className="font-semibold text-slate-950">Gmail connection</h3>
              {gmailStatus?.connected && gmailStatus.connectedEmail ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="min-w-0 text-sm text-slate-700"><span className="text-slate-500">Sending from</span> <strong className="break-all">{gmailStatus.connectedEmail}</strong></p>
                  <button type="button" disabled={busy} onClick={disconnectGmail} className="min-h-11 rounded border border-slate-300 px-3 text-sm">Disconnect Gmail</button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Connect Gmail to send from your own mailbox.</p>
                  {gmailStatus?.deliveryMode === "dry-run" && <p role="status" className="border-l-2 border-amber-500 pl-3 text-sm text-amber-900">Local dry-run: no email will be sent.</p>}
                  {gmailStatus?.configured ? <button type="button" disabled={busy} onClick={connectGmail} className="min-h-11 rounded bg-red-800 px-4 text-sm font-semibold text-white">Connect Gmail</button>
                    : <p className="text-sm text-slate-500">Gmail OAuth is not configured for this environment.</p>}
                </div>
              )}
            </section>
            <fieldset className="space-y-3">
              <legend className="font-semibold text-slate-950">Recipients</legend>
              {frozen.shareGrants.filter((grant: Grant) => grant.id && grant.recipient_email && !grant.revoked_at && !grant.revokedAt
                && (!(grant.expires_at ?? grant.expiresAt) || Date.parse((grant.expires_at ?? grant.expiresAt)!) > Date.now())).map((grant: Grant) => {
                const id = grant.id!;
                const checked = emailGrantIds.includes(id);
                return <label key={id} className={`flex min-h-14 cursor-pointer items-start gap-3 border px-3 py-3 ${checked ? "border-red-700 bg-red-50/50" : "border-slate-200"}`}>
                  <input type="checkbox" checked={checked} onChange={(event) => changeEmailRecipients(event.target.checked ? [...emailGrantIds, id] : emailGrantIds.filter((value) => value !== id))} className="mt-1" />
                  <span className="min-w-0"><span className="block break-words text-sm font-semibold text-slate-900">{grant.recipient_name ?? grant.recipientName}</span><span className="block break-all text-sm text-slate-600">{grant.recipient_email}</span></span>
                </label>;
              })}
              {!frozen.shareGrants.some((grant: Grant) => grant.recipient_email && !grant.revoked_at && !grant.revokedAt) && <p className="text-sm text-slate-500">No active report links with an email address are available.</p>}
            </fieldset>
            <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-slate-800">
              <input type="checkbox" checked={attachPdf} onChange={(event) => changeEmailAttachment(event.target.checked)} />
              <span>Attach PDF copy</span>
            </label>
            <section aria-label="Email preview" className="space-y-3 border-y border-slate-200 py-4">
              <h3 className="font-semibold text-slate-950">Email preview</h3>
              <p className="text-sm"><span className="text-slate-500">Subject</span><br /><strong>{emailPreview?.subject ?? `${frozen.report.terminology?.reportTitle ?? terminology.reportTitle} — ${frozen.report.property.address || frozen.report.property.listingReference || "your property"}`}</strong></p>
              {emailPreview?.filename && <p className="text-xs text-slate-500">PDF attachment: {emailPreview.filename}</p>}
              {emailPreview?.html ? <iframe title="Client email preview" sandbox="" srcDoc={emailPreview.html} className="h-[560px] w-full border border-slate-200 bg-white" /> : <p role="status" className="text-sm text-slate-500">Preparing email preview…</p>}
              {emailPreview?.text && <details><summary className="cursor-pointer text-sm font-medium text-slate-600">Plain-text version</summary><pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">{emailPreview.text}</pre></details>}
            </section>
            {emailResults.length > 0 && <section aria-label="Email results" className="space-y-2">{emailResults.map((result) => <p key={result.id} role={result.status === "FAILED" ? "alert" : "status"} className="border-l-2 border-emerald-700 pl-3 text-sm">{result.recipientName ? `${result.recipientName}: ` : ""}{result.status === "SUBMITTED_TEST" ? "Dry-run prepared; no email was sent." : result.status === "SUBMITTED" ? "Submitted to Gmail." : result.status === "SUBMISSION_UNKNOWN" ? "Gmail submission could not be confirmed. Do not retry automatically." : result.status === "FAILED" ? "Email send failed." : result.status}</p>)}</section>}
            <CommunicationHistory items={frozen.communications ?? []} />
            <button type="button" disabled={busy || !emailGrantIds.length || emailResults.length > 0 || (gmailStatus?.deliveryMode === "gmail" && (!gmailStatus.connected || !gmailStatus.configured))} onClick={sendReportEmail} className="min-h-11 rounded bg-red-800 px-5 text-sm font-semibold text-white disabled:opacity-40">{busy ? "Preparing email…" : "Send Email"}</button>
          </div>
        )}
      </SellerReportDrawer>
    </section>
  );
}
